<script setup lang="ts">
/** An internal note inside the chat timeline, at the time it was written. Staff only — never sent. */
import { computed, nextTick, ref } from 'vue'
import type { Note } from '../../api/collab/types'
import { clockTime } from '../../lib/inbox'
import { useCollab } from '../../stores/collab'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ note: Note; convId: string }>()
const collab = useCollab()

const editing = ref(false)
const draft = ref('')
const box = ref<HTMLTextAreaElement | null>(null)
const time = computed(() => clockTime(props.note.at))

function startEdit(): void {
  draft.value = props.note.text
  editing.value = true
  void nextTick(() => {
    box.value?.focus()
    autosize()
  })
}

function autosize(): void {
  const el = box.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`
}

async function save(): Promise<void> {
  const t = draft.value.trim()
  if (!t || t === props.note.text) {
    editing.value = false
    return
  }
  if (await collab.editNote(props.convId, props.note, t)) editing.value = false
}

function onKey(e: KeyboardEvent): void {
  if (e.isComposing) return
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    void save()
  } else if (e.key === 'Escape') {
    editing.value = false
  }
}
</script>

<template>
  <div class="nl" role="note" :aria-label="`โน้ตภายในจาก${note.author.name}`">
    <div class="nl-card">
      <div class="nl-h">
        <Icon name="note" />
        <b>โน้ตภายใน</b>
        <span class="nl-who">{{ note.author.name }}</span>
        <span class="nl-priv">เห็นเฉพาะพนักงาน</span>
        <span class="grow"></span>
        <span v-if="note.edited_at" class="nl-ed">แก้ไขแล้ว</span>
        <span class="mono nl-t">{{ time }}</span>
        <template v-if="note.can_edit && !editing">
          <button class="nl-b" aria-label="แก้โน้ต" title="แก้โน้ต" @click="startEdit"><Icon name="pencil" /></button>
          <button class="nl-b" aria-label="ลบโน้ต" title="ลบโน้ต" @click="collab.deleteNote(convId, note)"><Icon name="trash" /></button>
        </template>
      </div>
      <p v-if="!editing" class="nl-txt">{{ note.text }}</p>
      <div v-else class="nl-edit">
        <textarea ref="box" v-model="draft" rows="1" aria-label="แก้โน้ตภายใน" @input="autosize" @keydown="onKey"></textarea>
        <div class="nl-act">
          <button class="nl-x" @click="editing = false">ยกเลิก</button>
          <button class="nl-s" :disabled="!draft.trim()" @click="save">บันทึก</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nl {
  display: flex;
  justify-content: center;
}
.nl-card {
  width: min(560px, 100%);
  padding: 8px 12px 10px 14px;
  border-radius: 10px;
  background: #fffbe6;
  border: 1px solid #f1e3a6;
  box-shadow: inset 3px 0 0 #dfb325;
}
.nl-h {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 24px;
  font-size: 12px;
  line-height: 18px;
  color: #6f5a12;
  white-space: nowrap;
}
.nl-h > :deep(.ic) {
  width: 14px;
  height: 14px;
  color: #a37e0b;
}
.nl-h b {
  font-weight: 600;
}
.nl-who {
  font-weight: 600;
  color: #3d3320;
}
.nl-priv {
  color: #8a7430;
}
.nl-priv::before,
.nl-who::before {
  content: '·';
  margin-right: 6px;
  color: #b9a560;
}
.nl-ed,
.nl-t {
  color: #8a7430;
  font-size: 12px;
}
.nl-b {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #8a7430;
  opacity: 0;
  transition: opacity 0.12s;
}
.nl-card:hover .nl-b,
.nl-b:focus-visible {
  opacity: 1;
}
.nl-b:hover {
  background: #f6ebbd;
  color: #3d3320;
}
.nl-b :deep(.ic) {
  width: 14px;
  height: 14px;
}
.nl-txt {
  margin-top: 2px;
  font-size: 13.5px;
  line-height: 22px;
  color: #2f2a1c;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.nl-edit textarea {
  display: block;
  width: 100%;
  margin-top: 4px;
  padding: 6px 10px;
  border: 1px solid #e8d48a;
  border-radius: 8px;
  background: #fff;
  font-size: 13.5px;
  line-height: 22px;
  resize: none;
  outline: none;
}
.nl-edit textarea:focus {
  border-color: #c9a227;
}
.nl-act {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
}
.nl-x,
.nl-s {
  height: 28px;
  padding: 0 12px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 20px;
}
.nl-x {
  color: #6f5a12;
}
.nl-x:hover {
  background: #f6ebbd;
}
.nl-s {
  background: var(--warn);
  color: #fff;
}
.nl-s:disabled {
  opacity: 0.5;
}
</style>
